import { z } from 'zod';
import { amountSchema } from '../common/amount.js';
import { MAX_PAGE_SIZE } from '../common/pagination.js';
import { localeSchema } from '../common/primitives.js';
import { branchRefSchema, dateOnlySchema, employeeRefSchema } from '../common/refs.js';
/**
 * Shared pieces of the Debts contracts (debts.md sections 3-12). Amounts are
 * five-decimal strings; a positive balance means the customer owes
 * Al-Musafer, a negative one that Al-Musafer owes the customer.
 */
/** عليه رصيد (> 0), له رصيد (< 0), رصيد صفر. */
export const BALANCE_STATUSES = ['OWES', 'OWED', 'ZERO'];
export const balanceStatusSchema = z.enum(BALANCE_STATUSES);
/** مرتبط، بانتظار الربط، رقم غير صالح (debts.md section 10). */
export const LINK_STATUSES = ['LINKED', 'PENDING', 'INVALID_PHONE'];
export const linkStatusSchema = z.enum(LINK_STATUSES);
/** Both staff kinds and the two migration-only kinds (debts.md section 3). */
export const MOVEMENT_KINDS = ['DEBIT', 'CREDIT', 'MIGRATED', 'MIGRATED_ADJUSTMENT'];
export const movementKindSchema = z.enum(MOVEMENT_KINDS);
/** The kinds an employee enters: مدين / دائن. */
export const ENTERED_MOVEMENT_KINDS = ['DEBIT', 'CREDIT'];
export const enteredMovementKindSchema = z.enum(ENTERED_MOVEMENT_KINDS);
/**
 * The نوع الحركة filter: مدين and دائن also match the migrated movements that
 * carry a مدين or دائن value; تسوية مُرحَّلة matches the migrated adjustments.
 */
export const MOVEMENT_KIND_FILTERS = ['DEBIT', 'CREDIT', 'MIGRATED_ADJUSTMENT'];
export const movementKindFilterSchema = z.enum(MOVEMENT_KIND_FILTERS);
/** قيد الإرسال، تم الإرسال، تم التسليم، تمت القراءة، فشل الإرسال، رقم غير صالح. */
export const MESSAGE_STATUSES = [
    'PENDING',
    'SENT',
    'DELIVERED',
    'READ',
    'FAILED',
    'INVALID_PHONE',
];
export const messageStatusSchema = z.enum(MESSAGE_STATUSES);
/** `من تاريخ / إلى تاريخ`: Asia/Amman calendar days, both inclusive. */
export const periodQueryShape = {
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
};
/** Refuses a period whose start is after its end. */
export function periodIsOrdered(value) {
    return !value.from || !value.to || value.from <= value.to;
}
export const PERIOD_ORDER_ISSUE = {
    message: 'from must not be after to',
    path: ['to'],
};
/** Page and page size of the Debts lists (sorting is per list). */
export const pageQueryShape = {
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(MAX_PAGE_SIZE).default(20),
};
/** Exports carry the interface language, so a plain download link works. */
export const exportLocaleShape = { locale: localeSchema.default('ar') };
/** The debtor of an account, as shown in lists (العميل). */
export const debtorRefSchema = z.object({ id: z.string(), name: z.string() });
/** The account a movement belongs to, as shown in movement lists (العميل، الحساب). */
export const accountRefSchema = z.object({
    id: z.string(),
    number: z.number().int(),
    /** اسم الحساب. */
    name: z.string(),
    debtor: debtorRefSchema,
});
/** A movement row of the overview, the reports and their exports. */
export const ledgerRowSchema = z.object({
    id: z.string(),
    number: z.number().int(),
    reference: z.string(),
    kind: movementKindSchema,
    occurredAt: z.string(),
    account: accountRefSchema,
    statement: z.string().nullable(),
    debit: amountSchema,
    credit: amountSchema,
    balanceAfter: amountSchema,
    branch: branchRefSchema,
    /** The employee who entered it; null for migrated rows ("—"). */
    employee: employeeRefSchema.nullable(),
    notes: z.string().nullable(),
    edited: z.boolean(),
});
/** مجموع المدين / مجموع الدائن and the movement count. */
export const movementTotalsSchema = z.object({
    debit: amountSchema,
    credit: amountSchema,
    count: z.number().int().nonnegative(),
});
//# sourceMappingURL=common.js.map